/**
 * The 2D assembly stage: hosts the SVG canvas + chip overlay, wires pointer
 * events into the assembler's gesture engine, and owns zoom and pan exactly
 * like the room editor's stage (editor-core's reconcileViewport): the level's
 * rooms fitted and centered is the minimum zoom; zoomed in, the view pans
 * within that window and shows the minimap, and moving rooms never moves the
 * view (beyond clamping it back into the window). Switching levels starts
 * over at the fit.
 */

import { css } from '@emotion/react';
import {
  applyWheelToViewport,
  type AssemblerMutableState,
  createWheelClassifier,
  endAssemblerDrag,
  fitAssemblyViewport,
  levelBounds,
  levelRooms,
  moveAssemblerDrag,
  reconcileViewport,
  sameViewport,
  useFloorPlanAssemblerState,
  useFloorPlanPreferences,
  type Viewport,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { screenToModelPoint } from '../../pointerLib';
import { Minimap } from '../Minimap';
import { AssemblyDimsOverlay } from './AssemblyDimsOverlay';
import { AssemblyPlanSvg, AssemblyRoomsLayer } from './AssemblyPlanSvg';
import { assemblyPointerDown } from './assemblyPointer';

const STAGE_SVG_STYLE = css`
  label: ASSEMBLY_STAGE_SVG_STYLE;
  position: absolute; inset: 0; width: 100%; height: 100%; display: block;
  touch-action: none;
`;

const DIMS_OVERLAY_STYLE = css`
  label: ASSEMBLY_DIMS_OVERLAY_STYLE;
  position: absolute; inset: 0; pointer-events: none;
  overflow: hidden; /* a chip never paints past the stage, over the panel beside it */
`;

/** The minimum-zoom viewport for the current level on this stage. */
const fitFor = (st: AssemblerMutableState, stageW: number, stageH: number): Viewport => fitAssemblyViewport(levelBounds(st), stageW, stageH, st.viewport);

export interface AssemblyPlanStageProps {
  stageW: number;
  stageH: number;
  visible: boolean;
}

export function AssemblyPlanStage({ stageW, stageH, visible }: AssemblyPlanStageProps): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const { st, mutate, stRef } = useFloorPlanAssemblerState();
  const { layers } = useFloorPlanPreferences();
  const fit = useMemo(() => fitFor(st, stageW, stageH), [st.rooms, st.draftRooms, st.level, stageW, stageH]);
  const shownRef = useRef<{ fit: Viewport | null; level: number }>({ fit: null, level: st.level });

  useLayoutEffect(() => {
    if (!visible || st.drag) return;
    const previousFit = shownRef.current.level === st.level ? shownRef.current.fit : null;
    shownRef.current = { fit, level: st.level };
    const next = reconcileViewport(st.viewport, previousFit, fit, stageW, stageH);
    if (!sameViewport(next, st.viewport)) {
      mutate(s => {
        s.viewport = next;
      });
    }
  }, [visible, fit, st.level, st.drag, stageW, stageH, st.viewport, mutate]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    // A chip or panel field may still hold focus; blur it now so its commit
    // lands against the current selection before this click changes it.
    const active = document.activeElement;
    if (active instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) active.blur();
    const handle = (e.target as Element).closest('[data-handle]')?.getAttribute('data-handle') ?? null;
    if (handle) e.preventDefault();
    const nativeEvent = e.nativeEvent;
    mutate(s => assemblyPointerDown(s, handle, screenToModelPoint(s.viewport, svgEl, nativeEvent)));
  }, [mutate]);

  // Window-level move/up so drags keep tracking outside the SVG bounds.
  useEffect(() => {
    if (!visible) return;
    const onMove = (e: PointerEvent) => {
      const svgEl = svgRef.current;
      if (!svgEl || !stRef.current?.drag) return;
      mutate(s => moveAssemblerDrag(s, screenToModelPoint(s.viewport, svgEl, e)));
    };
    const onUp = () => {
      if (!stRef.current?.drag) return;
      mutate(s => endAssemblerDrag(s));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [visible, mutate, stRef]);

  // Wheel zoom/pan — a native, non-passive listener (React's wheel handlers are passive).
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || !visible) return;
    const classifyWheel = createWheelClassifier();
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svgEl.getBoundingClientRect();
      const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const wheel = {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        device: classifyWheel(e),
      };
      mutate(s => {
        if (s.drag) return;
        s.viewport = applyWheelToViewport(s.viewport, fitFor(s, stageW, stageH), wheel, anchor, stageW, stageH);
      });
    };
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, [visible, stageW, stageH, mutate]);

  return (
    <>
      <svg
        ref={svgRef}
        css={STAGE_SVG_STYLE}
        xmlns='http://www.w3.org/2000/svg'
        style={{ display: visible ? '' : 'none' }}
        onPointerDown={onPointerDown}
        data-testid='assembly-plan'
      >
        {visible && <AssemblyPlanSvg st={st} stageW={stageW} stageH={stageH} />}
      </svg>
      <div css={DIMS_OVERLAY_STYLE} style={{ display: visible ? '' : 'none' }}>
        {visible && <AssemblyDimsOverlay stageW={stageW} stageH={stageH} />}
      </div>
      {visible && (
        <Minimap
          fit={fit}
          viewport={st.viewport}
          stageW={stageW}
          stageH={stageH}
          onViewport={viewport =>
            mutate(s => {
              s.viewport = viewport;
            })}
        >
          {(miniViewport, width, height) => <AssemblyRoomsLayer rooms={levelRooms(st)} vp={miniViewport} stageW={width} stageH={height} layers={layers} />}
        </Minimap>
      )}
    </>
  );
}
